import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import { ArrowRight, ShieldCheck, Video, Truck, Check, CalendarDays, Package, Award } from "lucide-react";
import { toast } from "sonner";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Track", "Business", "Affiliations", "Owner Account", "Verified"];

/** Vendor onboarding — 2-track model.
 *  Track = 'technical' (serialized calendar) | 'supplies' (quantity counter).
 *  Post-signup dashboard routes on `vendor_classification`.
 */
export default function OnboardVendor() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [tracks, setTracks] = useState({});
  const [track, setTrack]   = useState(null);
  const [caps, setCaps]     = useState(new Set());

  const [legal, setLegal]     = useState("");
  const [brand, setBrand]     = useState("");
  const [address, setAddress] = useState("");
  const [gstin, setGstin]     = useState("");
  const [pan, setPan]         = useState("");
  const [ownerName, setOwnerName]   = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerPwd, setOwnerPwd]     = useState("");
  const [sectionAffiliations, setSectionAffiliations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/meta/vendor-tracks`).then(({ data }) => setTracks(data.tracks || {}));
  }, []);

  const pickTrack = (t) => { setTrack(t); setCaps(new Set()); };
  const toggleCap = (key) => setCaps((p) => {
    const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/onboard/org`, {
        org_type: "vendor",
        legal_name: legal,
        brand_name: brand || legal,
        registered_address: address,
        gstin, pan,
        owner_name: ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_password: ownerPwd,
        vendor_classification: track,
        vendor_capabilities: Array.from(caps),
        section_affiliations: sectionAffiliations,
      });
      await setToken(data.access_token);
      await refresh();
      setStep(4);
      toast.success(`Welcome — ${data.organization.brand_name} is verified`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setSubmitting(false);
    }
  };

  const trackCfg = tracks[track];

  return (
    <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="onboard-vendor-page">
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Vendor · Sign Up</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">List Your Services</h1>

      <Stepper steps={STEPS} step={step} />

      {/* STEP 0 · Track picker + capabilities */}
      {step === 0 && (
        <Card title="What do you provide?" testid="step-track">
          <p className="text-xs text-white/55 mb-4 max-w-lg">
            Vendors on Call Sheet split into two operational models. Pick the one
            that matches how your inventory actually moves.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="track-grid">
            <TrackCard
              active={track === "technical"}
              onClick={() => pickTrack("technical")}
              icon={<Video size={18} />}
              accent="#F5B82E"
              title="Technical Equipment"
              subtitle="Serialized inventory · 6-month availability grid"
              footer="Camera bodies · lenses · lights · grip · sound · DIT"
              testid="track-technical"
            />
            <TrackCard
              active={track === "supplies"}
              onClick={() => pickTrack("supplies")}
              icon={<Truck size={18} />}
              accent="#D9383A"
              title="Supplies & Logistics"
              subtitle="Quantity counter · stock ledger · dispatch flows"
              footer="Catering · Vanity vans · Set comfort · Hardware/Paint · Props · Consumables"
              testid="track-supplies"
            />
          </div>

          {trackCfg && (
            <div className="mt-6" data-testid="capability-grid">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 flex items-center gap-2">
                {track === "technical"
                  ? <CalendarDays size={11} className="text-[#F5B82E]" />
                  : <Package size={11} className="text-[#D9383A]" />}
                Select Capabilities · <span className="text-white/40">{caps.size} chosen</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {trackCfg.capabilities.map((c) => {
                  const on = caps.has(c.key);
                  return (
                    <button key={c.key} onClick={() => toggleCap(c.key)}
                      className={`px-3.5 py-2 text-xs rounded-full border transition-all inline-flex items-center gap-2
                        ${on
                          ? track === "technical"
                            ? "bg-[#F5B82E]/15 border-[#F5B82E]/60 text-[#F5B82E]"
                            : "bg-[#D9383A]/15 border-[#D9383A]/60 text-[#D9383A]"
                          : "bg-black/30 border-white/15 text-white/70 hover:border-white/35"}`}
                      data-testid={`cap-${c.key}`}>
                      {on && <Check size={13} />}
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <NavRow onNext={next} disabledNext={!track || caps.size === 0} />
        </Card>
      )}

      {/* STEP 1 · Business info */}
      {step === 1 && (
        <Card title="Business Details" testid="step-business">
          <Field label="Legal Name *" tid="vendor-legal">
            <Input value={legal} onChange={(e) => setLegal(e.target.value)}
              placeholder="e.g. Alpha Cine Rentals Pvt Ltd"
              className="h-11 bg-black/40 border-white/15" data-testid="vendor-legal-input" />
          </Field>
          <Field label="Brand Name" tid="vendor-brand">
            <Input value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g. Alpha Cine"
              className="h-11 bg-black/40 border-white/15" data-testid="vendor-brand-input" />
          </Field>
          <Field label="Registered Address" tid="vendor-address">
            <Textarea value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Building, Street, City, State, PIN"
              className="bg-black/40 border-white/15" data-testid="vendor-address-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="GSTIN" tid="vendor-gstin">
              <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="27ABCDE1234F1Z5" maxLength={15}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="vendor-gstin-input" />
            </Field>
            <Field label="PAN" tid="vendor-pan">
              <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F" maxLength={10}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="vendor-pan-input" />
            </Field>
          </div>
          <NavRow onBack={back} onNext={next} disabledNext={!legal} />
        </Card>
      )}

      {/* STEP 2 · Industry Affiliations (optional) */}
      {step === 2 && (
        <Card title="Industry Affiliations" testid="step-vendor-affiliations">
          <div className="flex items-start gap-3 mb-3 p-3 rounded-lg bg-indigo-500/8 border border-indigo-500/30">
            <Award size={16} className="text-indigo-300 shrink-0 mt-0.5" />
            <div className="text-xs text-white/75 leading-relaxed">
              <span className="text-indigo-200 font-semibold">Optional but recommended.</span>{" "}
              Certifications and body memberships (SIRVA · EESA · OEM authorisations)
              boost your ranking in producer searches and unlock the &quot;Vetted&quot; badge.
            </div>
          </div>
          <AffiliationsPicker
            section="vendor"
            value={sectionAffiliations}
            onChange={setSectionAffiliations}
            testidPrefix="onboard-aff-vendor"
          />
          <NavRow onBack={back} onNext={next} />
        </Card>
      )}

      {/* STEP 3 · Owner Credentials */}
      {step === 3 && (
        <Card title="Owner Account" testid="step-vendor-owner">
          <Field label="Full Name *" tid="vendor-owner-name">
            <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="vendor-owner-name-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email *" tid="vendor-owner-email">
              <Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                className="h-11 bg-black/40 border-white/15" data-testid="vendor-owner-email-input" />
            </Field>
            <Field label="Phone" tid="vendor-owner-phone">
              <Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="+91 …"
                className="h-11 bg-black/40 border-white/15" data-testid="vendor-owner-phone-input" />
            </Field>
          </div>
          <Field label="Password * (min 6 characters)" tid="vendor-owner-pwd">
            <Input type="password" value={ownerPwd} onChange={(e) => setOwnerPwd(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="vendor-owner-pwd-input" />
          </Field>
          <NavRow onBack={back} onSubmit={submit} submitting={submitting}
            submitLabel="Register Vendor"
            disabledNext={!ownerName || !ownerEmail || ownerPwd.length < 6} />
        </Card>
      )}

      {/* STEP 4 · Success */}
      {step === 4 && (
        <Card testid="step-vendor-success">
          <div className="text-center py-6" data-testid="vendor-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-tight mt-5">Vendor Listed</h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">
              You&rsquo;re now live. {track === "technical"
                ? "Add your first serialized assets and start reserving them against bookings."
                : "Set up your SKU catalog with reorder thresholds — we\u2019ll flag low stock automatically."}
            </p>
            <Button onClick={() => nav("/dashboard")}
              className="mt-7 btn-themed h-11 px-7 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
              data-testid="vendor-go-dashboard">
              Open Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function TrackCard({ active, onClick, icon, accent, title, subtitle, footer, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left p-5 rounded-xl border transition-all
        ${active
          ? "bg-white/[0.06] border-white/40"
          : "bg-white/[0.02] border-white/10 hover:border-white/25"}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
           style={{ background: `${accent}20`, color: accent }}>
        {icon}
      </div>
      <div className="font-display uppercase tracking-tight text-lg">{title}</div>
      <div className="text-xs text-white/60 mt-1">{subtitle}</div>
      <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-white/45">{footer}</div>
      {active && (
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase"
             style={{ color: accent }}>
          <Check size={12} /> Selected
        </div>
      )}
    </button>
  );
}
